import {
  Box3,
  Matrix4,
  Vector3,
  Euler,
  InstancedMesh,
  DynamicDrawUsage,
  Color,
  BufferAttribute,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { SetChildrenDepthWrite, modify } from '../../../helpers/three';
import { applySurfaceShading } from './streaming/surfaceShading';

export default class Loader {
  constructor(viewer, glbs, ReactAPI, isIfcProject) {
    this.viewer = viewer;
    this.urlsModel = glbs;
    this.ReactAPI = ReactAPI;
    this.isIfcProject = isIfcProject;

    this.modelProps = {
      scale: new Vector3(1, 1, 1),
      position: new Vector3(0, 0, 0),
      rotation: new Euler(),
      mapMeshes: new Map(),
      meshes: [],
      created: false,
      accumulatedBounds: new Box3(),
    };

    this.loadAll();
  }

  async loadAll() {
    try {
      for (const url of this.urlsModel.glbs) {
        await this.loadModel(url);
      }
      this.finalizeLoading();
    } catch (err) {
      console.error('Error loading models:', err);
      this.ReactAPI.setError(true);
    }
  }

  loadModel(glbUrl) {
    const gltfLoader = new GLTFLoader();
    const viewer = this.viewer;
    const reactapi = this.ReactAPI;
    const isIfcProject = this.isIfcProject;
    const modelProps = this.modelProps;

    return new Promise((resolve, reject) => {
      gltfLoader.load(
        glbUrl,
        gltf => {
          try {
            const model = gltf.scene;

            if (modelProps.created) {
              model.position.set(modelProps.position.x, modelProps.position.y, modelProps.position.z);
              model.rotation.set(modelProps.rotation.x, modelProps.rotation.y, modelProps.rotation.z);
              model.scale.set(modelProps.scale.x, modelProps.scale.y, modelProps.scale.z);
            } else if (!modelProps.created && model.children?.length > 0) {
              console.log('isIFCProject from loader', isIfcProject);
              model.position.set(0, 0, 0);

              if (isIfcProject) {
                model.rotation.x = Math.PI;
                model.rotation.z = Math.PI;
                model.rotation.y = Math.PI;
              } else {
                model.rotation.x = -Math.PI / 2;
              }

              modelProps.position.set(0, 0, 0);
              modelProps.rotation.x = isIfcProject ? Math.PI : -Math.PI / 2;
              if (isIfcProject) {
                modelProps.rotation.y = Math.PI;
                modelProps.rotation.z = Math.PI;
              }
              modelProps.scale.set(1, 1, 1);
              modelProps.created = true;
            }

            viewer.scene.add(model);
            viewer.loadedModels.push(model);

            this.expandInstancesFromMetadata(model);

            model.traverse(item => {
              item.layers.set(0);
              SetChildrenDepthWrite(item);
            });

            reactapi.setPercentsLoaded(100);
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        progressEvent => {
          if (progressEvent.total) {
            reactapi.setPercentsLoaded(
              Math.floor((progressEvent.loaded * 100) / progressEvent.total)
            );
          }
        },
        err => {
          reactapi.setError(true);
          reject(err);
        }
      );
    });
  }

  extractInstances(node) {
    const ud = node.userData;
    if (!ud) return [];

    const looksLikeEntry = v =>
      v && typeof v === 'object' &&
      (v.transformation || v.Transformation) &&
      (v.uniqueId || v.UniqueId);

    const tryCollect = source => {
      if (!source) return null;
      if (Array.isArray(source)) {
        const f = source.filter(looksLikeEntry);
        return f.length > 0 ? f : null;
      }
      if (typeof source === 'string') {
        try { return tryCollect(JSON.parse(source)); } catch { return null; }
      }
      if (typeof source === 'object') {
        if (looksLikeEntry(source)) return [source];
        const v = Object.values(source).filter(looksLikeEntry);
        if (v.length > 0) return v;
      }
      return null;
    };

    return tryCollect(ud.instances)
      ?? tryCollect(ud.gltf?.extras?.instances)
      ?? tryCollect(ud.extras?.instances)
      ?? tryCollect(ud)
      ?? [];
  }

  normalizeEntry(data) {
    const t = data.transformation ?? data.Transformation;
    const uniqueId = data.uniqueId ?? data.UniqueId;
    if (!t || !uniqueId) return null;
    const vals = [
      t.m0, t.m1, t.m2, t.m3, t.m4, t.m5, t.m6, t.m7,
      t.m8, t.m9, t.m10, t.m11, t.m12, t.m13, t.m14, t.m15,
    ];
    if (vals.some(v => !Number.isFinite(v))) return null;
    return { matrixValues: vals, uniqueId, raw: { ...data, uniqueId } };
  }

  // Per-fragment InstancedMesh. Same matrix set across fragments for a
  // given instance i → all fragments of element N align.
  //
  // mapMeshes records EVERY fragment for a uniqueId in `fragments`. The
  // legacy `mesh`/`index` fields keep pointing at fragment 0 so any old
  // reader that ignores `fragments` still works on at least one piece.
  expandInstancesFromMetadata(root) {
    const metadataParents = [];
    root.traverse(node => {
      const instances = this.extractInstances(node);
      if (instances.length > 0) metadataParents.push({ parent: node, instances });
    });

    const matrix = new Matrix4();
    let fragmentsInstanced = 0;
    let totalPlaced = 0;
    let totalSkipped = 0;

    metadataParents.forEach(({ parent, instances }) => {
      const meshFragments = [];
      parent.traverse(n => { if (n.isMesh && n !== parent) meshFragments.push(n); });
      if (parent.isMesh) meshFragments.unshift(parent);
      if (meshFragments.length === 0) return;

      const entries = [];
      instances.forEach(data => {
        const n = this.normalizeEntry(data);
        if (!n) { totalSkipped++; return; }
        entries.push(n);
      });
      if (entries.length === 0) return;

      const count = entries.length;
      const grandparent = parent.parent;

      meshFragments.forEach((fragment, fragIdx) => {
        const instanced = new InstancedMesh(
          fragment.geometry,
          // The same shading patch the streaming path applies. Without
          // it, a model drawn by THIS loader ignores the display
          // settings entirely - the toggle changes a uniform no shader
          // on screen reads, which is exactly "I turn it on and nothing
          // happens" (2026-09-20).
          applySurfaceShading(fragment.material),
          count
        );
        instanced.instanceMatrix.setUsage(DynamicDrawUsage);
        instanced.frustumCulled = false;
        instanced.name = fragment.name || `${parent.name}_frag${fragIdx}`;

        const perInstanceData = [];
        const color = new Color();

        entries.forEach((entry, i) => {
          const v = entry.matrixValues;
          matrix.set(
            v[0], v[1], v[2], v[3],
            v[4], v[5], v[6], v[7],
            v[8], v[9], v[10], v[11],
            v[12], v[13], v[14], v[15]
          );
          instanced.setMatrixAt(i, matrix);
          if (instanced.setColorAt) instanced.setColorAt(i, color);

          perInstanceData.push({
            ...entry.raw,
            id: entry.uniqueId,
            index: i,
          });

          // Multi-fragment registration. Every (mesh, index) pair that
          // belongs to this uniqueId lands in `fragments`. The first one
          // also seeds `mesh` / `index` for legacy single-fragment readers.
          let rec = this.modelProps.mapMeshes.get(entry.uniqueId);
          if (!rec) {
            rec = {
              mesh: instanced,
              uniqueId: entry.uniqueId,
              id: entry.uniqueId,
              index: i,
              fragments: [],
            };
            this.modelProps.mapMeshes.set(entry.uniqueId, rec);
          }
          rec.fragments.push({ mesh: instanced, index: i });
        });

        instanced.instanceMatrix.needsUpdate = true;
        if (instanced.instanceColor) instanced.instanceColor.needsUpdate = true;
        instanced.userData = perInstanceData;

        // Snapshot canonical matrices for visibility math. Stored in two
        // places: `instanced.centeredMatrices` for legacy readers, and
        // `instanced.userData.centeredMatrices` because
        // applyInstancedMeshVisibility reads from userData and the prior
        // version skipped non-frag-0 meshes during isolate.
        const centered = new Float32Array(count * 16);
        for (let i = 0; i < count; i++) {
          instanced.getMatrixAt(i, matrix);
          matrix.toArray(centered, i * 16);
        }
        instanced.centeredMatrices = centered;
        instanced.userData.centeredMatrices = centered;

        SetChildrenDepthWrite(instanced);
        instanced.layers.set(0);

        (grandparent || root).add(instanced);
        this.modelProps.meshes.push(instanced);
        fragmentsInstanced++;
        if (fragIdx === 0) totalPlaced += count;

        try {
          const b = this.calculateInstanceBounds(instanced);
          if (!b.isEmpty()) this.modelProps.accumulatedBounds.union(b);
        } catch (e) {
          console.error('Error calculating instance bounds:', e);
        }
      });

      if (parent.parent) parent.parent.remove(parent);
    });

    console.group('[Loader] Instancing summary');
    console.log(`Metadata-carrying parents: ${metadataParents.length}`);
    console.log(`InstancedMesh fragments created: ${fragmentsInstanced}`);
    console.log(`Instances placed: ${totalPlaced} | Skipped: ${totalSkipped}`);
    console.groupEnd();

    // Same contract as the streaming loader: the model's bounds in viewer
    // space, for consumers that must sit at the model (the ground grid).
    if (this.viewer) this.viewer.modelBounds = this.modelProps.accumulatedBounds;
  }

  calculateInstanceBounds(instancedMesh) {
    if (!instancedMesh || !instancedMesh.geometry) return new Box3();
    const geometry = instancedMesh.geometry;
    const box = new Box3();
    const instanceBox = new Box3();
    const matrix = new Matrix4();

    if (!geometry.boundingBox) geometry.computeBoundingBox();
    if (!geometry.boundingBox) return box;

    for (let i = 0; i < instancedMesh.count; i++) {
      instancedMesh.getMatrixAt(i, matrix);
      instanceBox.copy(geometry.boundingBox);
      instanceBox.applyMatrix4(matrix);
      box.union(instanceBox);
    }
    return box;
  }

  finalizeLoading() {
    this.viewer.meshesObject = this.modelProps.mapMeshes;
    this.viewer.meshes = this.modelProps.meshes;

    this.viewer.scene.updateMatrixWorld(true);
    if (this.viewer.renderer) {
      // One place decides how a frame is drawn (viewer.drawFrame).
      if (this.viewer.drawFrame) this.viewer.drawFrame();
      else this.viewer.renderer.render(this.viewer.scene, this.viewer.camera);
    }
    if (this.viewer.animate) this.viewer.animate();

    this.ReactAPI.setLoading(false);
    this.ReactAPI.setAllowToResize(true);
  }

  generateUVs(geometry) {
    const position = geometry.attributes.position;
    if (!position) return;
    const uvs = new Float32Array(position.count * 2);
    for (let i = 0; i < position.count; i++) {
      uvs[i * 2] = (position.getX(i) + 1) * 0.5;
      uvs[i * 2 + 1] = (position.getY(i) + 1) * 0.5;
    }
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  }

  simplifyMesh(geometry, targetVertexCount) {
    if (!geometry?.attributes?.position ||
        !Number.isInteger(targetVertexCount) ||
        targetVertexCount <= 0) {
      console.error('Invalid geometry or target vertex count');
      return null;
    }
    try {
      return modify(geometry, targetVertexCount);
    } catch (e) {
      console.error('Mesh simplification failed:', e);
      return null;
    }
  }
}